import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import BrandLogo from "../Common/BrandLogo";
import "./CommercialDemo.css";

const BillyMascot3D = lazy(() => import("../Onboarding/BillyMascot3D"));

const BILLY_LAUNCHER_SRC = "/images/billy/billy-helper-launcher.png";
const BILLY_FALLBACK_SRC = "/images/billy/billy-helper-fallback.png";
const BILLY_MODEL_SRC = "/models/billy/billy-helper-optimized-draco-animation-fixed.glb";
const BILLY_FALLBACK_MODEL_SRC = "/models/billy/billy-helper.glb";

const SCENES = [
  {
    id: "opening",
    label: "Mascot Intro",
    routeLabel: "/",
    duration: 12000,
    view: "hero",
    animationName: "Billy_Hero_Wave",
    spotlight: "logo",
    camera: "hero",
    billy: {
      x: 54,
      y: 57,
      scale: 1.72,
      facing: -1,
      tilt: -1,
      gesture: "wave",
      lookTarget: "viewer",
      bubble: "hero",
      entrance: { x: -26, y: 58, scale: 1.68, facing: 1 },
    },
    cues: [
      { at: 0.24, text: "Hey, I'm Billy." },
      { at: 0.5, text: "Welcome to MyFarmingPal." },
      { at: 0.76, text: "Let me show you around." },
    ],
  },
  {
    id: "community",
    label: "Community Board",
    routeLabel: "/community",
    duration: 8500,
    view: "community",
    animationName: "Billy_Present_Right",
    spotlight: "community-post",
    camera: "board",
    billy: {
      x: 17,
      y: 61,
      scale: 1.24,
      facing: 1,
      tilt: 2,
      gesture: "present-right",
      lookTarget: "post-card",
      bubble: "right",
    },
    cues: [
      { at: 0, text: "Start with your local farming community." },
      { at: 0.5, text: "Useful posts stay easy to find without pay-to-play reach." },
    ],
  },
  {
    id: "market",
    label: "Local Marketplace",
    routeLabel: "/home?view=market",
    duration: 8000,
    view: "market",
    animationName: "Billy_Present_Left",
    spotlight: "market-card",
    camera: "market",
    billy: {
      x: 74,
      y: 61,
      scale: 1.22,
      facing: -1,
      tilt: -2,
      gesture: "present-left",
      lookTarget: "market-card",
      bubble: "left",
    },
    cues: [
      { at: 0, text: "Find local products without digging through a dozen apps." },
    ],
  },
  {
    id: "plants",
    label: "Plants & Garden",
    routeLabel: "/plants-garden",
    duration: 7600,
    view: "plants",
    animationName: "Billy_Hoof_Lift",
    spotlight: "plants-card",
    camera: "plants",
    billy: {
      x: 18,
      y: 60,
      scale: 1.2,
      facing: 1,
      tilt: 1,
      gesture: "hoof-lift",
      lookTarget: "plants-card",
      bubble: "right",
    },
    cues: [
      { at: 0, text: "Plants, starts, and garden goods have their own space too." },
    ],
  },
  {
    id: "jobs",
    label: "Jobs",
    routeLabel: "/map",
    duration: 9000,
    view: "jobs",
    animationName: "Billy_Present_Left",
    spotlight: "field-map",
    camera: "map",
    billy: {
      x: 75,
      y: 62,
      scale: 1.23,
      facing: -1,
      tilt: -1,
      gesture: "present-left",
      lookTarget: "map",
      bubble: "left",
    },
    cues: [
      { at: 0, text: "Post the work. Find nearby help. Keep the details in one place." },
    ],
  },
  {
    id: "tools",
    label: "Farm Tools",
    routeLabel: "/farm-records",
    duration: 7200,
    view: "tools",
    animationName: "Billy_Present_Right",
    spotlight: "tools-grid",
    camera: "tools",
    billy: {
      x: 18,
      y: 61,
      scale: 1.16,
      facing: 1,
      tilt: 2,
      gesture: "montage",
      lookTarget: "tool-grid",
      bubble: "right",
    },
    cues: [
      { at: 0, text: "Your farm tools belong together." },
    ],
  },
  {
    id: "ending",
    label: "Ending",
    routeLabel: "/",
    duration: 7600,
    view: "ending",
    animationName: "Billy_Hero_Wave",
    spotlight: "ending-logo",
    camera: "hero",
    billy: {
      x: 66,
      y: 58,
      scale: 1.56,
      facing: -1,
      tilt: 0,
      gesture: "wave",
      lookTarget: "viewer",
      bubble: "left",
    },
    cues: [
      { at: 0, text: "MyFarmingPal" },
      { at: 0.38, text: "Built to bring local farming communities back together." },
      { at: 0.68, text: "Founder early access is open." },
    ],
  },
];

const getSceneDuration = (scene, speed) => Math.max(1200, Math.round(scene.duration / speed));

const ACTOR_DEFAULT_ANIMATION = "Billy_Idle";
const ACTOR_WALK_ANIMATION = "Billy_Walk_Loop";
const ACTOR_MIN_X = 8;
const ACTOR_MAX_X = 92;
const ACTOR_MIN_Y = 24;
const ACTOR_MAX_Y = 82;

const clampValue = (value, min, max) => Math.max(min, Math.min(max, value));

const getActorBillyForScene = (scene) => {
  const billy = scene.billy || {};
  return {
    x: billy.x ?? 50,
    y: billy.y ?? 60,
    scale: billy.scale ?? 1.2,
    facing: billy.facing || 1,
    tilt: billy.tilt || 0,
    gesture: billy.gesture || "idle",
    lookTarget: billy.lookTarget || "viewer",
    bubble: billy.bubble || "right",
  };
};

const clampActorBilly = (billy) => ({
  ...billy,
  x: clampValue(billy.x, ACTOR_MIN_X, ACTOR_MAX_X),
  y: clampValue(billy.y, ACTOR_MIN_Y, ACTOR_MAX_Y),
  scale: clampValue(billy.scale || 1, 0.86, 1.9),
});

const PUPPET_STORAGE_KEY = "mfp-localhost-billy-puppet-studio";
const PUPPET_PERFORMANCE_STORAGE_KEY = "mfp-localhost-billy-puppet-performance";

const DEFAULT_PUPPET_POSE = {
  bodyLeanX: 0,
  bodyLeanY: 0,
  bodyTwist: 0,
  chestTwist: 0,
  headYaw: 0,
  headPitch: 0,
  headTilt: 0,
  neckYaw: 0,
  neckPitch: 0,
  leftHoofLift: 0,
  rightHoofLift: 0,
  leftPresent: 0,
  rightPresent: 0,
  rearStance: 0,
  weightShift: 0,
  jawOpen: 0,
  idleAmount: 0.35,
};

const PUPPET_POSE_PRESETS = {
  Neutral: DEFAULT_PUPPET_POSE,
  "Friendly Wave": { ...DEFAULT_PUPPET_POSE, rightHoofLift: 0.85, headTilt: -8, headYaw: 8, bodyLeanX: 5 },
  "Present Left": { ...DEFAULT_PUPPET_POSE, rightPresent: 0.88, headYaw: -16, neckYaw: -10, bodyLeanX: -8 },
  "Present Right": { ...DEFAULT_PUPPET_POSE, leftPresent: 0.88, headYaw: 16, neckYaw: 10, bodyLeanX: 8 },
  "Hoof Lift": { ...DEFAULT_PUPPET_POSE, rightHoofLift: 0.8, headTilt: -6, bodyLeanX: 5 },
  "Curious Head Tilt": { ...DEFAULT_PUPPET_POSE, headTilt: 18, headYaw: -8, neckYaw: -4, jawOpen: 0.18 },
  "Confident Nod": { ...DEFAULT_PUPPET_POSE, headPitch: 12, neckPitch: 5, bodyLeanY: 4 },
  "Excited Lean": { ...DEFAULT_PUPPET_POSE, bodyLeanY: -10, chestTwist: 8, rightHoofLift: 0.35, jawOpen: 0.25 },
  "Hero Ending": { ...DEFAULT_PUPPET_POSE, rightHoofLift: 0.65, headPitch: -4, bodyLeanX: -4, idleAmount: 0.48 },
  "Walk-In Ready": { ...DEFAULT_PUPPET_POSE, bodyLeanY: -4, weightShift: -0.35, rearStance: 0.25 },
  "Off-Screen Left": { ...DEFAULT_PUPPET_POSE },
  "Off-Screen Right": { ...DEFAULT_PUPPET_POSE },
};

const PUPPET_SLIDERS = [
  ["bodyLeanX", "Lean L/R", -22, 22, 1],
  ["bodyLeanY", "Lean F/B", -18, 18, 1],
  ["bodyTwist", "Body Turn", -20, 20, 1],
  ["chestTwist", "Chest Twist", -24, 24, 1],
  ["headYaw", "Head Yaw", -38, 38, 1],
  ["headPitch", "Head Pitch", -24, 24, 1],
  ["headTilt", "Head Tilt", -28, 28, 1],
  ["neckYaw", "Neck Yaw", -26, 26, 1],
  ["neckPitch", "Neck Pitch", -18, 18, 1],
  ["leftHoofLift", "Left Hoof", 0, 1, 0.01],
  ["rightHoofLift", "Right Hoof", 0, 1, 0.01],
  ["leftPresent", "Present L", 0, 1, 0.01],
  ["rightPresent", "Present R", 0, 1, 0.01],
  ["rearStance", "Rear Stance", -1, 1, 0.01],
  ["weightShift", "Weight", -1, 1, 0.01],
  ["jawOpen", "Mouth", 0, 1, 0.01],
  ["idleAmount", "Subtle Idle", 0, 1, 0.01],
];

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mediaQuery) return undefined;

    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);

    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  return prefersReducedMotion;
};

const useQueryFlags = () =>
  useMemo(() => {
    if (typeof window === "undefined") {
      return {
        actorMode: false,
        autostart: false,
        clean: false,
        debug: false,
        motionOverride: false,
        puppetStudio: false,
        recording: false,
        speed: 1,
      };
    }

    const params = new URLSearchParams(window.location.search);
    const speed = Number(params.get("speed") || 1);
    const autostart = params.get("autostart") === "1";
    const actorMode = params.get("actorMode") === "1";
    const puppetStudio = params.get("puppetStudio") === "1";
    const clean = params.get("clean") === "1";
    const debug = params.get("demoDebug") === "1" || params.get("commercialDebug") === "1";

    return {
      actorMode,
      autostart,
      clean,
      debug,
      motionOverride: params.get("motion") === "1",
      puppetStudio,
      recording: clean || params.get("recording") === "1" || (autostart && !debug && !actorMode && !puppetStudio),
      speed: Number.isFinite(speed) && speed > 0 ? Math.min(speed, 4) : 1,
    };
  }, []);

const ProductSwatch = ({ tone = "hay" }) => <span className={`commercial-product-swatch ${tone}`} />;

const Pill = ({ children, className = "" }) => (
  <span className={`commercial-pill ${className}`.trim()}>{children}</span>
);

const Spotlight = ({ target }) => (
  <span className={`commercial-feature-spotlight spotlight-${target}`} aria-hidden="true" />
);

const HeroIntroView = ({ spotlight }) => (
  <section className="commercial-view commercial-hero-intro-view" aria-label="Billy introduction screen">
    <Spotlight target={spotlight} />
    <div className="commercial-hero-brand">
      <BrandLogo className="commercial-hero-logo" />
      <h1>Meet Billy.</h1>
      <p>The friendly guide for local farming, farm work, and community tools.</p>
    </div>
    <div className="commercial-hero-stage-line" aria-hidden="true" />
  </section>
);

const HomeView = ({ spotlight }) => (
  <section className="commercial-view commercial-home-view" aria-label="MyFarmingPal homepage preview">
    <Spotlight target={spotlight} />
    <div className="commercial-hero-panel commercial-target-logo">
      <BrandLogo className="commercial-hero-logo" />
      <h1>Local farming, all in one place.</h1>
      <p>Browse community posts, market goods, jobs, tools, events, and farm resources from one familiar dashboard.</p>
      <div className="commercial-home-actions">
        <button type="button">Browse Market</button>
        <button type="button">Open Community</button>
      </div>
    </div>
    <div className="commercial-home-grid" aria-hidden="true">
      <div className="commercial-mini-card active">
        <strong>Community</strong>
        <span>Local questions and updates</span>
      </div>
      <div className="commercial-mini-card">
        <strong>Farm Market</strong>
        <span>Goods, pricing, and messages</span>
      </div>
      <div className="commercial-mini-card">
        <strong>Jobs</strong>
        <span>Field work and local help</span>
      </div>
      <div className="commercial-mini-card">
        <strong>Farm Tools</strong>
        <span>Records and planning</span>
      </div>
    </div>
  </section>
);

const CommunityView = ({ spotlight }) => (
  <section className="commercial-view" aria-label="Community board preview">
    <Spotlight target={spotlight} />
    <div className="commercial-section-head">
      <div>
        <span className="commercial-eyebrow">Community Board</span>
        <h2>Useful local posts, easy to scan.</h2>
      </div>
      <Pill>Categories</Pill>
    </div>
    <div className="commercial-community-layout">
      <div className="commercial-filter-rail">
        {["Questions", "Equipment", "Animals", "Weather", "Events"].map((item) => (
          <button type="button" key={item} className={item === "Questions" ? "active" : ""}>
            {item}
          </button>
        ))}
      </div>
      <div className="commercial-post-stack">
        <article className="commercial-post-card commercial-highlight commercial-target-community-post">
          <div className="commercial-post-meta">
            <Pill>Question</Pill>
            <span>Local area</span>
          </div>
          <h3>Best timing for first hay cut after heavy rain?</h3>
          <p>Neighbors can compare conditions, advice, and practical timing without chasing posts across different groups.</p>
          <div className="commercial-post-actions">
            <button type="button">Helpful</button>
            <button type="button">Comment</button>
            <button type="button">Report</button>
          </div>
        </article>
        <article className="commercial-post-card">
          <div className="commercial-post-meta">
            <Pill>Equipment</Pill>
            <span>Public post</span>
          </div>
          <h3>Looking for a used hay spear near town.</h3>
          <p>Replies stay attached to the post, so follow-up details do not disappear.</p>
        </article>
      </div>
    </div>
  </section>
);

const MarketView = ({ spotlight }) => (
  <section className="commercial-view" aria-label="Local marketplace preview">
    <Spotlight target={spotlight} />
    <div className="commercial-section-head">
      <div>
        <span className="commercial-eyebrow">Local Farm Market</span>
        <h2>Goods, distance, and seller messaging.</h2>
      </div>
      <Pill>Distance browsing</Pill>
    </div>
    <div className="commercial-product-grid">
      <article className="commercial-product-card commercial-highlight commercial-target-market-card">
        <ProductSwatch tone="hay" />
        <Pill>Made to Order</Pill>
        <h3>Orchard grass square bales</h3>
        <p className="commercial-price">Starts at $7</p>
        <p>12 mi away - Request-to-buy messaging</p>
      </article>
      <article className="commercial-product-card">
        <ProductSwatch tone="eggs" />
        <Pill>Farm goods</Pill>
        <h3>Pasture-raised eggs</h3>
        <p className="commercial-price">$5 dozen</p>
        <p>Pickup windows listed by seller</p>
      </article>
      <article className="commercial-product-card">
        <ProductSwatch tone="produce" />
        <Pill>Seasonal</Pill>
        <h3>Mixed produce box</h3>
        <p className="commercial-price">Starts at $18</p>
        <p>Message before pickup</p>
      </article>
    </div>
  </section>
);

const PlantsView = ({ spotlight }) => (
  <section className="commercial-view" aria-label="Plants and garden preview">
    <Spotlight target={spotlight} />
    <div className="commercial-section-head">
      <div>
        <span className="commercial-eyebrow">Plants & Garden</span>
        <h2>Garden listings get their own space.</h2>
      </div>
      <Pill>Seasonal availability</Pill>
    </div>
    <div className="commercial-product-grid plants">
      <article className="commercial-product-card">
        <ProductSwatch tone="starts" />
        <Pill>Starts</Pill>
        <h3>Tomato starts</h3>
        <p>Ready this weekend - Local pickup</p>
      </article>
      <article className="commercial-product-card commercial-highlight commercial-target-plants-card">
        <ProductSwatch tone="shrubs" />
        <Pill>Shrubs</Pill>
        <h3>Native berry shrubs</h3>
        <p>Seasonal availability listed clearly</p>
      </article>
      <article className="commercial-product-card">
        <ProductSwatch tone="garden" />
        <Pill>Coming Soon</Pill>
        <h3>Compost and garden soil</h3>
        <p>Listing preview for future availability</p>
      </article>
    </div>
  </section>
);

const JobsView = ({ spotlight }) => (
  <section className="commercial-view commercial-jobs-view" aria-label="Jobs and field map preview">
    <Spotlight target={spotlight} />
    <div className="commercial-section-head">
      <div>
        <span className="commercial-eyebrow">Jobs</span>
        <h2>Field jobs and day jobs in one workflow.</h2>
      </div>
      <Pill>Per-acre bidding</Pill>
    </div>
    <div className="commercial-map-layout">
      <div className="commercial-map commercial-highlight commercial-target-field-map" aria-hidden="true">
        <span className="commercial-map-grid" />
        <span className="commercial-field-shape one" />
        <span className="commercial-field-shape two" />
        <span className="commercial-map-pin" />
        <span className="commercial-map-label">Satellite map browsing</span>
      </div>
      <div className="commercial-job-stack">
        <article className="commercial-job-card">
          <Pill>Field job</Pill>
          <h3>Cut and bale back field</h3>
          <p>18.4 acres - Entrance marker - Bids by acre</p>
        </article>
        <article className="commercial-job-card">
          <Pill>Day job</Pill>
          <h3>Fence repair help needed</h3>
          <p>Local help listing - Message before work starts</p>
        </article>
      </div>
    </div>
  </section>
);

const ToolsView = ({ spotlight }) => (
  <section className="commercial-view" aria-label="Farm tools preview">
    <Spotlight target={spotlight} />
    <div className="commercial-section-head">
      <div>
        <span className="commercial-eyebrow">Farm Tools</span>
        <h2>Private records and public local tools together.</h2>
      </div>
      <Pill>Tool montage</Pill>
    </div>
    <div className="commercial-tool-grid commercial-target-tools-grid">
      {[
        ["Farm Records", "Tasks, crops, and notes"],
        ["Herd Book", "Animal records"],
        ["Almanac", "Planning and seasonal info"],
        ["Land Share", "Land opportunities"],
        ["Events", "Local farm happenings"],
        ["Farm Stands", "Nearby pickup spots"],
      ].map(([title, text], index) => (
        <article
          className={`commercial-tool-card ${index === 1 || index === 4 ? "commercial-highlight" : ""}`}
          key={title}
        >
          <span className="commercial-tool-icon" aria-hidden="true">
            {index + 1}
          </span>
          <strong>{title}</strong>
          <p>{text}</p>
        </article>
      ))}
    </div>
  </section>
);

const EndingView = ({ spotlight }) => (
  <section className="commercial-view commercial-ending-view" aria-label="Commercial ending screen">
    <Spotlight target={spotlight} />
    <div className="commercial-ending-lockup commercial-target-ending-logo">
      <BrandLogo className="commercial-ending-logo" />
      <h1>MyFarmingPal</h1>
      <p>Built to bring local farming communities back together.</p>
      <strong>Founder early access is open.</strong>
      <span>myfarmingpal.com</span>
    </div>
  </section>
);

const VIEW_COMPONENTS = {
  hero: HeroIntroView,
  home: HomeView,
  community: CommunityView,
  market: MarketView,
  plants: PlantsView,
  jobs: JobsView,
  tools: ToolsView,
  ending: EndingView,
};

const CommercialDemo = () => {
  const queryFlags = useQueryFlags();
  const isActorMode = queryFlags.actorMode;
  const isPuppetStudio = queryFlags.puppetStudio;
  const isManualMode = isActorMode || isPuppetStudio;
  const prefersReducedMotion = usePrefersReducedMotion();
  const [started, setStarted] = useState(queryFlags.autostart || isManualMode);
  const [completed, setCompleted] = useState(false);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [cueIndex, setCueIndex] = useState(0);
  const [motionOverride, setMotionOverride] = useState(queryFlags.motionOverride);
  const [transitioning, setTransitioning] = useState(false);
  const [heroEntering, setHeroEntering] = useState(queryFlags.autostart && !isManualMode);
  const [billyReady, setBillyReady] = useState(false);
  const [actorBilly, setActorBilly] = useState(() => getActorBillyForScene(SCENES[0]));
  const [actorAnimation, setActorAnimation] = useState(ACTOR_DEFAULT_ANIMATION);
  const [actorGesture, setActorGesture] = useState(SCENES[0].billy?.gesture || "idle");
  const [actorMoving, setActorMoving] = useState(false);
  const [actorSpeechIndex, setActorSpeechIndex] = useState(0);
  const [actorSpeechVisible, setActorSpeechVisible] = useState(true);
  const [actorPanelVisible, setActorPanelVisible] = useState(!queryFlags.clean);
  const [puppetPanelVisible, setPuppetPanelVisible] = useState(!queryFlags.clean);
  const [puppetPose, setPuppetPose] = useState(DEFAULT_PUPPET_POSE);
  const [savedPuppetPoses, setSavedPuppetPoses] = useState({});
  const [rigDiagnostics, setRigDiagnostics] = useState(null);
  const [puppetDebug, setPuppetDebug] = useState(false);
  const [recorderState, setRecorderState] = useState("idle");
  const [performanceFrames, setPerformanceFrames] = useState([]);
  const [performanceCursor, setPerformanceCursor] = useState(0);
  const [webcamStatus, setWebcamStatus] = useState("disabled");
  const [actorDragging, setActorDragging] = useState(false);
  const timeoutRef = useRef(null);
  const cueTimeoutsRef = useRef([]);
  const actorKeysRef = useRef(new Set());
  const actorFrameRef = useRef(null);
  const actorActionTimerRef = useRef(null);
  const recorderStartRef = useRef(0);
  const recorderFrameRef = useRef(null);
  const playbackStartRef = useRef(0);
  const playbackIndexRef = useRef(0);
  const performanceFramesRef = useRef([]);
  const activeScene = SCENES[sceneIndex];
  const ActiveView = VIEW_COMPONENTS[activeScene.view] || HomeView;
  const allowMotion = !prefersReducedMotion || motionOverride;
  const progress = completed ? 100 : ((sceneIndex + (started ? 0.2 : 0)) / SCENES.length) * 100;
  const cue = activeScene.cues[cueIndex] || activeScene.cues[0];
  const billy = activeScene.billy || {};
  const entranceBilly = heroEntering && activeScene.id === "opening" ? billy.entrance : null;
  const visibleBilly = entranceBilly ? { ...billy, ...entranceBilly } : billy;
  const animationName = !started
    ? "Billy_Idle"
    : (transitioning || heroEntering) && allowMotion
    ? "Billy_Walk_Loop"
    : activeScene.animationName;
  const displayedBilly = isManualMode ? actorBilly : visibleBilly;
  const displayedGesture = isManualMode ? actorGesture : billy.gesture || "idle";
  const displayedAnimationName = isManualMode ? actorAnimation : animationName;
  const displayedCue = isManualMode
    ? actorSpeechVisible
      ? activeScene.cues[actorSpeechIndex] || activeScene.cues[0]
      : null
    : cue;
  const displayedLookTarget = isManualMode ? actorBilly.lookTarget : billy.lookTarget;
  const isBillyWalking = isManualMode ? actorMoving : transitioning || heroEntering;

  const clearTimers = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    cueTimeoutsRef.current.forEach((timerId) => window.clearTimeout(timerId));
    cueTimeoutsRef.current = [];
  };

  const clearActorActionTimer = () => {
    if (actorActionTimerRef.current) {
      window.clearTimeout(actorActionTimerRef.current);
      actorActionTimerRef.current = null;
    }
  };

  const resetActorForScene = (nextIndex = sceneIndex) => {
    const boundedIndex = Math.max(0, Math.min(nextIndex, SCENES.length - 1));
    const nextScene = SCENES[boundedIndex];
    clearTimers();
    clearActorActionTimer();
    actorKeysRef.current.clear();
    setStarted(true);
    setCompleted(false);
    setSceneIndex(boundedIndex);
    setCueIndex(0);
    setActorBilly(getActorBillyForScene(nextScene));
    setActorAnimation(ACTOR_DEFAULT_ANIMATION);
    setActorGesture(nextScene.billy?.gesture || "idle");
    setActorMoving(false);
    setActorSpeechIndex(0);
    setActorSpeechVisible(true);
  };

  const restoreActorIdle = (gesture = "idle") => {
    if (actorKeysRef.current.size > 0) return;
    setActorAnimation(ACTOR_DEFAULT_ANIMATION);
    setActorGesture(gesture);
    setActorMoving(false);
  };

  const playActorAction = (animation, gesture = "idle", options = {}) => {
    clearActorActionTimer();
    actorKeysRef.current.clear();
    setActorMoving(false);
    setActorGesture(gesture);
    setActorBilly((currentBilly) =>
      clampActorBilly({
        ...currentBilly,
        facing: options.facing || currentBilly.facing,
        tilt: options.tilt ?? currentBilly.tilt,
        lookTarget: options.lookTarget || currentBilly.lookTarget,
        bubble: options.bubble || currentBilly.bubble,
      })
    );

    setActorAnimation(ACTOR_DEFAULT_ANIMATION);
    window.setTimeout(() => setActorAnimation(animation), 20);

    if (options.duration) {
      actorActionTimerRef.current = window.setTimeout(
        () => restoreActorIdle(options.idleGesture || "idle"),
        options.duration
      );
    }
  };

  const advanceActorSpeech = (direction = 1) => {
    const cueCount = activeScene.cues.length;
    if (!cueCount) return;
    setActorSpeechVisible(true);
    setActorSpeechIndex((currentIndex) => (currentIndex + direction + cueCount) % cueCount);
  };

  const setScene = (nextIndex) => {
    clearTimers();
    setCompleted(false);
    setSceneIndex(Math.max(0, Math.min(nextIndex, SCENES.length - 1)));
    setCueIndex(0);
    setStarted(true);
    setHeroEntering(nextIndex === 0);
    if (allowMotion) {
      setTransitioning(true);
      window.setTimeout(() => setTransitioning(false), 2200);
      if (nextIndex === 0) {
        window.setTimeout(() => setHeroEntering(false), 3200);
      }
    }
  };

  const startTour = () => {
    clearTimers();
    setSceneIndex(0);
    setCueIndex(0);
    setCompleted(false);
    setStarted(true);
    setHeroEntering(true);
    if (allowMotion) {
      setTransitioning(true);
      window.setTimeout(() => setTransitioning(false), 2200);
      window.setTimeout(() => setHeroEntering(false), 3200);
    }
  };

  const finishTour = () => {
    clearTimers();
    setCompleted(true);
    setCueIndex(Math.max(0, activeScene.cues.length - 1));
  };

  const skipTour = () => {
    clearTimers();
    setScene(SCENES.length - 1);
    setCompleted(true);
  };

  useEffect(() => {
    if (isManualMode) return undefined;
    if (!queryFlags.autostart || !started || activeScene.id !== "opening") return undefined;
    if (!allowMotion) {
      setHeroEntering(false);
      return undefined;
    }

    const transitionTimer = window.setTimeout(() => setTransitioning(false), 2200);
    const entranceTimer = window.setTimeout(() => setHeroEntering(false), 3200);

    return () => {
      window.clearTimeout(transitionTimer);
      window.clearTimeout(entranceTimer);
    };
  }, [activeScene.id, allowMotion, isManualMode, queryFlags.autostart, started]);

  useEffect(() => {
    if (isManualMode) return undefined;
    if (!started || completed) return undefined;

    clearTimers();
    setCueIndex(0);

    const duration = getSceneDuration(activeScene, queryFlags.speed);
    activeScene.cues.forEach((nextCue, nextCueIndex) => {
      if (nextCueIndex === 0) return;
      const timerId = window.setTimeout(() => {
        setCueIndex(nextCueIndex);
      }, Math.max(0, Math.round(duration * nextCue.at)));
      cueTimeoutsRef.current.push(timerId);
    });

    timeoutRef.current = window.setTimeout(() => {
      if (sceneIndex >= SCENES.length - 1) {
        setCompleted(true);
        return;
      }

      setScene(sceneIndex + 1);
    }, duration);

    return clearTimers;
  }, [activeScene, completed, isManualMode, queryFlags.speed, sceneIndex, started]);

  useEffect(() => {
    if (!isManualMode || !started) return undefined;

    let lastTime = 0;
    const tickActorMovement = (time) => {
      const keys = actorKeysRef.current;
      const movingLeft = keys.has("a");
      const movingRight = keys.has("d");
      const movingUp = keys.has("w");
      const movingDown = keys.has("s");

      if (lastTime && keys.size > 0) {
        const delta = Math.min(50, time - lastTime) / 16.67;
        const dx = (movingRight ? 0.32 : 0) - (movingLeft ? 0.32 : 0);
        const dy = (movingDown ? 0.24 : 0) - (movingUp ? 0.24 : 0);

        if (dx || dy) {
          setActorBilly((currentBilly) =>
            clampActorBilly({
              ...currentBilly,
              x: currentBilly.x + dx * delta,
              y: currentBilly.y + dy * delta,
              facing: dx < 0 ? -1 : dx > 0 ? 1 : currentBilly.facing,
              tilt: dx < 0 ? -2 : dx > 0 ? 2 : currentBilly.tilt,
            })
          );
        }
      }

      lastTime = time;
      actorFrameRef.current = window.requestAnimationFrame(tickActorMovement);
    };

    actorFrameRef.current = window.requestAnimationFrame(tickActorMovement);

    return () => {
      if (actorFrameRef.current) {
        window.cancelAnimationFrame(actorFrameRef.current);
        actorFrameRef.current = null;
      }
    };
  }, [isManualMode, started]);

  useEffect(() => {
    if (!isManualMode || !started) return undefined;

    const isEditableTarget = (target) => {
      const tagName = target?.tagName?.toLowerCase();
      return tagName === "input" || tagName === "textarea" || tagName === "select" || target?.isContentEditable;
    };

    const handleKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;
      const key = event.key.toLowerCase();

      if (["w", "a", "s", "d"].includes(key)) {
        event.preventDefault();
        clearActorActionTimer();
        actorKeysRef.current.add(key);
        setActorMoving(true);
        setActorAnimation(ACTOR_WALK_ANIMATION);
        setActorGesture("idle");
        setActorBilly((currentBilly) =>
          clampActorBilly({
            ...currentBilly,
            facing: key === "a" ? -1 : key === "d" ? 1 : currentBilly.facing,
            tilt: key === "a" ? -2 : key === "d" ? 2 : currentBilly.tilt,
          })
        );
        return;
      }

      if (/^[1-7]$/.test(key)) {
        event.preventDefault();
        resetActorForScene(Number(key) - 1);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        advanceActorSpeech(event.shiftKey ? -1 : 1);
        return;
      }

      if (event.key === "Backspace") {
        event.preventDefault();
        setActorSpeechVisible(false);
        return;
      }

      if (key === "h") {
        event.preventDefault();
        if (isPuppetStudio) {
          setPuppetPanelVisible((isVisible) => !isVisible);
        } else if (!queryFlags.clean) {
          setActorPanelVisible((isVisible) => !isVisible);
        }
        return;
      }

      if (key === "r") {
        event.preventDefault();
        resetActorForScene(sceneIndex);
        return;
      }

      if (key === " ") {
        event.preventDefault();
        playActorAction("Billy_Hero_Wave", "wave", {
          duration: 2600,
          idleGesture: activeScene.billy?.gesture || "idle",
          lookTarget: "viewer",
        });
        return;
      }

      if (key === "q") {
        event.preventDefault();
        playActorAction("Billy_Present_Left", "present-left", {
          facing: -1,
          tilt: -2,
          duration: 2600,
          idleGesture: "present-left",
        });
        return;
      }

      if (key === "e") {
        event.preventDefault();
        playActorAction("Billy_Present_Right", "present-right", {
          facing: 1,
          tilt: 2,
          duration: 2600,
          idleGesture: "present-right",
        });
        return;
      }

      if (key === "f") {
        event.preventDefault();
        playActorAction("Billy_Hoof_Lift", "hoof-lift", {
          duration: 2400,
          idleGesture: "hoof-lift",
        });
        return;
      }

      if (key === "n") {
        event.preventDefault();
        playActorAction("Billy_Nod", "idle", {
          duration: 1600,
          idleGesture: activeScene.billy?.gesture || "idle",
          lookTarget: "viewer",
        });
        return;
      }

      if (key === "t") {
        event.preventDefault();
        playActorAction("Billy_Look_Around", "idle", {
          duration: 2200,
          idleGesture: activeScene.billy?.gesture || "idle",
        });
        return;
      }

      if (key === "i") {
        event.preventDefault();
        restoreActorIdle(activeScene.billy?.gesture || "idle");
      }
    };

    const handleKeyUp = (event) => {
      const key = event.key.toLowerCase();
      if (!["w", "a", "s", "d"].includes(key)) return;
      event.preventDefault();
      actorKeysRef.current.delete(key);

      if (actorKeysRef.current.size === 0) {
        restoreActorIdle(activeScene.billy?.gesture || "idle");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [activeScene, isManualMode, isPuppetStudio, queryFlags.clean, sceneIndex, started]);

  const handleActorPointerDown = (event) => {
    if (!isManualMode) return;
    event.preventDefault();
    clearActorActionTimer();
    actorKeysRef.current.clear();
    setActorMoving(false);
    setActorDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);

    const updateActorFromPointer = (clientX, clientY) => {
      const nextX = (clientX / window.innerWidth) * 100;
      const nextY = (clientY / window.innerHeight) * 100;
      setActorBilly((currentBilly) =>
        clampActorBilly({
          ...currentBilly,
          x: nextX,
          y: nextY,
        })
      );
    };

    updateActorFromPointer(event.clientX, event.clientY);

    const handlePointerMove = (moveEvent) => {
      updateActorFromPointer(moveEvent.clientX, moveEvent.clientY);
    };

    const handlePointerUp = () => {
      setActorDragging(false);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  };

  const updatePuppetPoseValue = (key, value) => {
    setPuppetPose((currentPose) => ({
      ...currentPose,
      [key]: Number(value),
    }));
  };

  const applyPuppetPreset = (label) => {
    const preset = savedPuppetPoses[label] || PUPPET_POSE_PRESETS[label];
    if (!preset) return;
    setPuppetPose({ ...DEFAULT_PUPPET_POSE, ...preset });

    if (label === "Off-Screen Left") {
      setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, x: ACTOR_MIN_X, y: 58, facing: 1 }));
    }
    if (label === "Off-Screen Right") {
      setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, x: ACTOR_MAX_X, y: 58, facing: -1 }));
    }
  };

  const saveCustomPuppetPose = () => {
    const label = `Custom ${Object.keys(savedPuppetPoses).length + 1}`;
    const nextPoses = { ...savedPuppetPoses, [label]: puppetPose };
    setSavedPuppetPoses(nextPoses);
    window.localStorage?.setItem(PUPPET_STORAGE_KEY, JSON.stringify(nextPoses));
  };

  const resetPuppetPose = () => {
    setPuppetPose(DEFAULT_PUPPET_POSE);
    setActorAnimation(ACTOR_DEFAULT_ANIMATION);
    setActorGesture(activeScene.billy?.gesture || "idle");
  };

  const getPuppetFrame = (timeMs = 0) => ({
    t: Math.max(0, Math.round(timeMs)),
    sceneIndex,
    actorBilly,
    actorAnimation,
    actorGesture,
    actorSpeechIndex,
    actorSpeechVisible,
    puppetPose,
  });

  const applyPuppetFrame = (frame) => {
    if (!frame) return;
    setSceneIndex(Math.max(0, Math.min(frame.sceneIndex || 0, SCENES.length - 1)));
    setActorBilly(clampActorBilly({ ...getActorBillyForScene(SCENES[frame.sceneIndex || 0]), ...(frame.actorBilly || {}) }));
    setActorAnimation(frame.actorAnimation || ACTOR_DEFAULT_ANIMATION);
    setActorGesture(frame.actorGesture || "idle");
    setActorSpeechIndex(frame.actorSpeechIndex || 0);
    setActorSpeechVisible(frame.actorSpeechVisible !== false);
    setPuppetPose({ ...DEFAULT_PUPPET_POSE, ...(frame.puppetPose || {}) });
  };

  const startPuppetRecording = () => {
    if (!isPuppetStudio) return;
    performanceFramesRef.current = [];
    setPerformanceFrames([]);
    recorderStartRef.current = performance.now();
    setRecorderState("recording");
  };

  const stopPuppetRecording = () => {
    if (recorderFrameRef.current) {
      window.cancelAnimationFrame(recorderFrameRef.current);
      recorderFrameRef.current = null;
    }
    const nextFrames = performanceFramesRef.current;
    setPerformanceFrames(nextFrames);
    if (nextFrames.length) {
      window.localStorage?.setItem(PUPPET_PERFORMANCE_STORAGE_KEY, JSON.stringify(nextFrames));
    }
    setRecorderState("idle");
  };

  const playPuppetPerformance = () => {
    const frames = performanceFrames.length ? performanceFrames : performanceFramesRef.current;
    if (!frames.length) return;
    playbackStartRef.current = performance.now();
    playbackIndexRef.current = 0;
    setRecorderState("playing");
  };

  const pausePuppetPerformance = () => {
    if (recorderFrameRef.current) {
      window.cancelAnimationFrame(recorderFrameRef.current);
      recorderFrameRef.current = null;
    }
    setRecorderState("paused");
  };

  const resetPuppetPerformance = () => {
    if (recorderFrameRef.current) {
      window.cancelAnimationFrame(recorderFrameRef.current);
      recorderFrameRef.current = null;
    }
    performanceFramesRef.current = [];
    setPerformanceFrames([]);
    setPerformanceCursor(0);
    setRecorderState("idle");
    window.localStorage?.removeItem(PUPPET_PERFORMANCE_STORAGE_KEY);
  };

  const exportPuppetPerformance = () => {
    const payload = {
      version: 1,
      createdAt: new Date().toISOString(),
      frames: performanceFrames.length ? performanceFrames : performanceFramesRef.current,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "billy-puppet-performance.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const importPuppetPerformance = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || "{}"));
        const frames = Array.isArray(parsed.frames) ? parsed.frames : Array.isArray(parsed) ? parsed : [];
        performanceFramesRef.current = frames;
        setPerformanceFrames(frames);
        window.localStorage?.setItem(PUPPET_PERFORMANCE_STORAGE_KEY, JSON.stringify(frames));
      } catch (error) {
        console.warn("Billy puppet performance import failed:", error.message || error);
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleWebcamToggle = () => {
    setWebcamStatus((currentStatus) => (currentStatus === "disabled" ? "planned" : "disabled"));
  };

  useEffect(() => {
    if (!isPuppetStudio) return;
    try {
      const savedPoses = JSON.parse(window.localStorage?.getItem(PUPPET_STORAGE_KEY) || "{}");
      if (savedPoses && typeof savedPoses === "object") setSavedPuppetPoses(savedPoses);
      const savedFrames = JSON.parse(window.localStorage?.getItem(PUPPET_PERFORMANCE_STORAGE_KEY) || "[]");
      if (Array.isArray(savedFrames)) {
        performanceFramesRef.current = savedFrames;
        setPerformanceFrames(savedFrames);
      }
    } catch (error) {
      console.warn("Billy Puppet Studio local storage load failed:", error.message || error);
    }
  }, [isPuppetStudio]);

  useEffect(() => {
    if (recorderState !== "recording") return undefined;

    let lastSample = 0;
    const recordFrame = (now) => {
      if (now - lastSample >= 100) {
        lastSample = now;
        const frame = getPuppetFrame(now - recorderStartRef.current);
        performanceFramesRef.current = [...performanceFramesRef.current, frame];
        setPerformanceCursor(frame.t);
      }
      recorderFrameRef.current = window.requestAnimationFrame(recordFrame);
    };

    recorderFrameRef.current = window.requestAnimationFrame(recordFrame);

    return () => {
      if (recorderFrameRef.current) {
        window.cancelAnimationFrame(recorderFrameRef.current);
        recorderFrameRef.current = null;
      }
    };
  }, [actorAnimation, actorBilly, actorGesture, actorSpeechIndex, actorSpeechVisible, puppetPose, recorderState, sceneIndex]);

  useEffect(() => {
    if (recorderState !== "playing") return undefined;
    const frames = performanceFrames.length ? performanceFrames : performanceFramesRef.current;
    if (!frames.length) {
      setRecorderState("idle");
      return undefined;
    }

    const playFrame = (now) => {
      const elapsed = now - playbackStartRef.current;
      while (playbackIndexRef.current < frames.length - 1 && frames[playbackIndexRef.current + 1].t <= elapsed) {
        playbackIndexRef.current += 1;
      }

      const frame = frames[playbackIndexRef.current];
      applyPuppetFrame(frame);
      setPerformanceCursor(Math.round(elapsed));

      if (playbackIndexRef.current >= frames.length - 1) {
        setRecorderState("idle");
        return;
      }

      recorderFrameRef.current = window.requestAnimationFrame(playFrame);
    };

    recorderFrameRef.current = window.requestAnimationFrame(playFrame);

    return () => {
      if (recorderFrameRef.current) {
        window.cancelAnimationFrame(recorderFrameRef.current);
        recorderFrameRef.current = null;
      }
    };
  }, [performanceFrames, recorderState]);

  useEffect(
    () => () => {
      clearTimers();
      clearActorActionTimer();
      if (actorFrameRef.current) {
        window.cancelAnimationFrame(actorFrameRef.current);
      }
      if (recorderFrameRef.current) {
        window.cancelAnimationFrame(recorderFrameRef.current);
      }
    },
    []
  );

  return (
    <main
      className={`commercial-demo ${motionOverride ? "motion-override" : ""}`}
      data-view={activeScene.view}
      data-scene={activeScene.id}
      data-camera={activeScene.camera}
      data-spotlight={activeScene.spotlight}
      data-recording={queryFlags.recording ? "true" : "false"}
      data-actor-mode={isActorMode ? "true" : "false"}
      data-puppet-studio={isPuppetStudio ? "true" : "false"}
      data-actor-dragging={actorDragging ? "true" : "false"}
    >
      {!started && (
        <section className="commercial-start" aria-label="Start Billy's commercial tour">
          <div className="commercial-start-panel">
            <BrandLogo className="commercial-start-logo" />
            <h1>Billy's MyFarmingPal Tour</h1>
            <p>A localhost-only walkthrough built for vertical commercial recording.</p>
            <div className="commercial-start-actions">
              <button type="button" className="btn-primary" onClick={startTour}>
                Start Billy's Tour
              </button>
              {completed && (
                <button type="button" onClick={startTour}>
                  Replay
                </button>
              )}
            </div>
            {prefersReducedMotion && (
              <label className="commercial-motion-toggle">
                <input
                  type="checkbox"
                  checked={motionOverride}
                  onChange={(event) => setMotionOverride(event.target.checked)}
                />
                Recording motion override
              </label>
            )}
          </div>
        </section>
      )}

      {started && (
        <>
          <div className="commercial-demo-chrome">
            <header className="commercial-demo-header">
              <BrandLogo className="commercial-demo-logo" />
              {queryFlags.debug && (
                <div className="commercial-route-pill" aria-label="Demo route">
                  {activeScene.routeLabel}
                </div>
              )}
            </header>

            {queryFlags.debug && (
              <nav className="commercial-scene-tabs" aria-label="Tour progress">
                {SCENES.map((scene, index) => (
                  <button
                    type="button"
                    key={scene.id}
                    className={index === sceneIndex ? "active" : ""}
                    onClick={() => setScene(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>
            )}

            <div className="commercial-progress" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>

            <div className="commercial-screen" aria-live="polite">
              <ActiveView spotlight={activeScene.spotlight} />
            </div>
          </div>

          <div
            className={`commercial-billy ${isBillyWalking ? "is-walking" : ""} ${
              billyReady ? "is-ready" : "is-loading"
            } gesture-${displayedGesture} bubble-${displayedBilly.bubble || "left"}`}
            style={{
              "--billy-x": `${displayedBilly.x}%`,
              "--billy-y": `${displayedBilly.y}%`,
              "--billy-scale": displayedBilly.scale,
              "--billy-facing": displayedBilly.facing || 1,
              "--billy-tilt": `${displayedBilly.tilt || 0}deg`,
            }}
            data-look-target={displayedLookTarget}
            onPointerDown={handleActorPointerDown}
          >
            {displayedCue && (
              <div className="commercial-billy-bubble">
                <span key={`${activeScene.id}-${isManualMode ? actorSpeechIndex : cueIndex}`}>
                  {displayedCue.text}
                </span>
              </div>
            )}
            <span className="commercial-billy-gesture" aria-hidden="true" />
            <span className="commercial-billy-ground" aria-hidden="true" />
            <div className="commercial-billy-model-shell">
              <Suspense
                fallback={
                  <img
                    className="commercial-billy-fallback"
                    src={BILLY_LAUNCHER_SRC}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                }
              >
                <BillyMascot3D
                  enabled
                  modelSrc={BILLY_MODEL_SRC}
                  fallbackModelSrc={BILLY_FALLBACK_MODEL_SRC}
                  fallbackSrc={BILLY_FALLBACK_SRC}
                  className="commercial-billy-model"
                  animationName={displayedAnimationName}
                  prefersReducedMotion={!allowMotion}
                  onReady={() => setBillyReady(true)}
                  puppetPose={isPuppetStudio ? puppetPose : null}
                  showSkeletonHelper={isPuppetStudio && puppetDebug && !queryFlags.clean}
                  onRigDiagnostics={isPuppetStudio ? setRigDiagnostics : undefined}
                />
              </Suspense>
            </div>
          </div>

          {!queryFlags.recording && !isManualMode && (
            <div className="commercial-control-bar">
              <span>
                {activeScene.label} - {sceneIndex + 1}/{SCENES.length}
              </span>
              <button type="button" onClick={skipTour}>
                Skip Tour
              </button>
              {completed && (
                <button type="button" className="btn-primary" onClick={startTour}>
                  Replay
                </button>
              )}
            </div>
          )}

          {queryFlags.debug && (
            <aside className="commercial-debug" aria-label="Commercial demo scene controls">
              <strong>Local scene index</strong>
              <div className="commercial-debug-grid">
                {SCENES.map((scene, index) => (
                  <button
                    type="button"
                    key={scene.id}
                    className={index === sceneIndex ? "active" : ""}
                    onClick={() => setScene(index)}
                  >
                    {index + 1}. {scene.label}
                  </button>
                ))}
              </div>
              <label className="commercial-motion-toggle">
                <input
                  type="checkbox"
                  checked={motionOverride}
                  onChange={(event) => setMotionOverride(event.target.checked)}
                />
                Recording motion override
              </label>
            </aside>
          )}

          {isActorMode && actorPanelVisible && !queryFlags.clean && (
            <aside className="commercial-actor-panel" aria-label="Billy actor mode controls">
              <div className="commercial-actor-panel-head">
                <div>
                  <strong>Billy Actor Mode</strong>
                  <span>
                    Scene {sceneIndex + 1}: {activeScene.label}
                  </span>
                </div>
                <button type="button" onClick={() => setActorPanelVisible(false)}>
                  Hide
                </button>
              </div>

              <div className="commercial-actor-meter">
                <span>X {Math.round(actorBilly.x)}</span>
                <span>Y {Math.round(actorBilly.y)}</span>
                <span>{actorAnimation.replace("Billy_", "")}</span>
              </div>

              <div className="commercial-actor-scenes">
                {SCENES.map((scene, index) => (
                  <button
                    type="button"
                    key={scene.id}
                    className={index === sceneIndex ? "active" : ""}
                    onClick={() => resetActorForScene(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <div className="commercial-actor-actions">
                <button
                  type="button"
                  onClick={() =>
                    playActorAction("Billy_Present_Left", "present-left", {
                      facing: -1,
                      tilt: -2,
                      duration: 2600,
                      idleGesture: "present-left",
                    })
                  }
                >
                  Q Left
                </button>
                <button
                  type="button"
                  onClick={() =>
                    playActorAction("Billy_Present_Right", "present-right", {
                      facing: 1,
                      tilt: 2,
                      duration: 2600,
                      idleGesture: "present-right",
                    })
                  }
                >
                  E Right
                </button>
                <button
                  type="button"
                  onClick={() => playActorAction("Billy_Hoof_Lift", "hoof-lift", { duration: 2400 })}
                >
                  F Hoof
                </button>
                <button
                  type="button"
                  onClick={() => playActorAction("Billy_Hero_Wave", "wave", { duration: 2600 })}
                >
                  Space Wave
                </button>
                <button type="button" onClick={() => playActorAction("Billy_Nod", "idle", { duration: 1600 })}>
                  N Nod
                </button>
                <button
                  type="button"
                  onClick={() => playActorAction("Billy_Look_Around", "idle", { duration: 2200 })}
                >
                  T Look
                </button>
              </div>

              <div className="commercial-actor-actions compact">
                <button type="button" onClick={() => advanceActorSpeech(-1)}>
                  Prev Line
                </button>
                <button type="button" onClick={() => advanceActorSpeech(1)}>
                  Next Line
                </button>
                <button type="button" onClick={() => setActorSpeechVisible((isVisible) => !isVisible)}>
                  Bubble
                </button>
                <button type="button" onClick={() => resetActorForScene(sceneIndex)}>
                  Reset
                </button>
              </div>

              <p>
                WASD walk. Drag Billy. 1-7 scenes. Enter advances speech, Shift+Enter goes back. Backspace hides the
                bubble. H hides this panel.
              </p>
            </aside>
          )}

          {isPuppetStudio && puppetPanelVisible && (
            <aside className="commercial-puppet-panel" aria-label="Billy Puppet Studio controls">
              <div className="commercial-actor-panel-head">
                <div>
                  <strong>Billy Puppet Studio</strong>
                  <span>
                    Scene {sceneIndex + 1}: {activeScene.label}
                  </span>
                </div>
                <button type="button" onClick={() => setPuppetPanelVisible(false)}>
                  Hide
                </button>
              </div>

              <div className="commercial-actor-meter">
                <span>X {Math.round(actorBilly.x)}</span>
                <span>Y {Math.round(actorBilly.y)}</span>
                <span>{recorderState}</span>
              </div>

              <div className="commercial-puppet-section">
                <h3>Stage</h3>
                <div className="commercial-actor-actions compact">
                  <button type="button" onClick={() => setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, x: currentBilly.x - 5, facing: -1 }))}>
                    Move L
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, x: currentBilly.x + 5, facing: 1 }))}>
                    Move R
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, y: currentBilly.y - 4 }))}>
                    Up
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, y: currentBilly.y + 4 }))}>
                    Down
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, scale: currentBilly.scale + 0.08 }))}>
                    Forward
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => clampActorBilly({ ...currentBilly, scale: currentBilly.scale - 0.08 }))}>
                    Back
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => ({ ...currentBilly, facing: -1 }))}>
                    Turn L
                  </button>
                  <button type="button" onClick={() => setActorBilly((currentBilly) => ({ ...currentBilly, facing: 1, tilt: 0 }))}>
                    Viewer
                  </button>
                </div>
              </div>

              <div className="commercial-puppet-section">
                <h3>Pose Presets</h3>
                <div className="commercial-puppet-presets">
                  {[...Object.keys(PUPPET_POSE_PRESETS), ...Object.keys(savedPuppetPoses)].map((label) => (
                    <button type="button" key={label} onClick={() => applyPuppetPreset(label)}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="commercial-actor-actions compact">
                  <button type="button" onClick={saveCustomPuppetPose}>
                    Save Pose
                  </button>
                  <button type="button" onClick={resetPuppetPose}>
                    Neutral
                  </button>
                  <button type="button" onClick={() => playActorAction("Billy_Walk_Loop", "idle")}>
                    Walk
                  </button>
                  <button type="button" onClick={() => playActorAction("Billy_Chew_Idle", "idle")}>
                    Idle
                  </button>
                </div>
              </div>

              <div className="commercial-puppet-section">
                <h3>Full Body</h3>
                <div className="commercial-puppet-sliders">
                  {PUPPET_SLIDERS.map(([key, label, min, max, step]) => (
                    <label key={key}>
                      <span>
                        {label}
                        <output>{Number(puppetPose[key] || 0).toFixed(step < 1 ? 2 : 0)}</output>
                      </span>
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={puppetPose[key] ?? 0}
                        onChange={(event) => updatePuppetPoseValue(key, event.target.value)}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="commercial-puppet-section">
                <h3>Performance</h3>
                <div className="commercial-actor-actions compact">
                  <button type="button" onClick={startPuppetRecording} disabled={recorderState === "recording"}>
                    Record
                  </button>
                  <button type="button" onClick={stopPuppetRecording} disabled={recorderState !== "recording"}>
                    Stop
                  </button>
                  <button type="button" onClick={playPuppetPerformance} disabled={!performanceFrames.length}>
                    Play
                  </button>
                  <button type="button" onClick={pausePuppetPerformance} disabled={recorderState !== "playing"}>
                    Pause
                  </button>
                  <button type="button" onClick={resetPuppetPerformance}>
                    Reset
                  </button>
                  <button type="button" onClick={exportPuppetPerformance} disabled={!performanceFrames.length}>
                    Export JSON
                  </button>
                </div>
                <label className="commercial-puppet-import">
                  Import JSON
                  <input type="file" accept="application/json,.json" onChange={importPuppetPerformance} />
                </label>
                <p>
                  {performanceFrames.length} frames - {Math.round(performanceCursor / 100) / 10}s
                </p>
              </div>

              <div className="commercial-puppet-section">
                <h3>Diagnostics</h3>
                <div className="commercial-actor-actions compact">
                  <button type="button" onClick={() => setPuppetDebug((isDebug) => !isDebug)}>
                    Skeleton
                  </button>
                  <button type="button" onClick={() => setActorSpeechVisible((isVisible) => !isVisible)}>
                    Bubble
                  </button>
                  <button type="button" onClick={() => advanceActorSpeech(1)}>
                    Next Line
                  </button>
                  <button type="button" onClick={handleWebcamToggle}>
                    Webcam
                  </button>
                </div>
                <p>
                  Bones: {rigDiagnostics?.boneCount || 0}. Safe roles: {rigDiagnostics?.availableRoles?.length || 0}.
                  Missing: {(rigDiagnostics?.missingRoles || []).filter((role) => role === "tail" || role.includes("Ear")).join(", ") || "none"}.
                  Webcam mode: {webcamStatus === "planned" ? "integration point only" : "disabled"}.
                </p>
              </div>

              <p>
                WASD walks, Q/E present, F hoof lift, Space wave, N nod, T look, 1-7 scenes, H hides or reveals this
                panel. Drag Billy for staging.
              </p>
            </aside>
          )}
        </>
      )}
    </main>
  );
};

export { SCENES };
export default CommercialDemo;
